// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Explore/Community Component
// ═══════════════════════════════════════════════════════════════════════════
// Shows trending verses, friends' shares, and community content
// ═══════════════════════════════════════════════════════════════════════════

import { icons } from './icons.js';
import { isAuthenticated, getCurrentUser } from '../services/auth.js';
import { showAuthPrompt } from './AuthPrompt.js';
import {
  getTrendingVerses,
  getRecentSharedVerses,
  getFriendsSharedVerses,
  likeSharedVerse,
  saveFavorite
} from '../services/firestore.js';
import { logExploreOpen } from '../services/analytics.js';

let currentOverlay = null;
let currentTab = 'trending';

// ─────────────────────────────────────────────────────────────────────────
// SHOW/HIDE EXPLORE
// ─────────────────────────────────────────────────────────────────────────

export function showExplore() {
  if (currentOverlay) return;

  const overlay = document.createElement('div');
  overlay.className = 'explore-overlay';
  overlay.id = 'explore-overlay';

  overlay.innerHTML = `
    <div class="explore-panel">
      <div class="explore-handle"></div>
      
      <div class="explore-header">
        <h2 class="explore-title">Explore</h2>
        <button class="explore-close-btn" id="explore-close">${icons.x}</button>
      </div>
      
      <div class="explore-tabs">
        <button class="explore-tab active" data-tab="trending">
          ${icons.trending}
          <span>Random</span>
        </button>
        <button class="explore-tab" data-tab="recent">
          ${icons.clock}
          <span>Recent</span>
        </button>
        <button class="explore-tab" data-tab="friends">
          ${icons.users}
          <span>Friends</span>
        </button>
      </div>
      
      <div class="explore-content" id="explore-content">
        <div class="explore-loading">
          <div class="explore-loading-spinner"></div>
          <p>Loading verses...</p>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);
  currentOverlay = overlay;

  // Animate in
  requestAnimationFrame(() => {
    overlay.classList.add('active');
  });

  // Setup events
  setupExploreEvents(overlay);

  // Load initial tab
  logExploreOpen('trending');
  loadTab('trending');
}

export function hideExplore() {
  if (!currentOverlay) return;

  currentOverlay.classList.remove('active');
  setTimeout(() => {
    currentOverlay?.remove();
    currentOverlay = null;
  }, 300);
}

// ─────────────────────────────────────────────────────────────────────────
// EVENT HANDLING
// ─────────────────────────────────────────────────────────────────────────

function setupExploreEvents(overlay) {
  // Close button
  overlay.querySelector('#explore-close')?.addEventListener('click', hideExplore);

  // Backdrop click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) hideExplore();
  });

  // Tab switching
  overlay.querySelectorAll('.explore-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      const tabName = tab.dataset.tab;
      switchTab(tabName);
    });
  });
}

function switchTab(tabName) {
  currentTab = tabName;

  // Update active tab styling
  currentOverlay?.querySelectorAll('.explore-tab').forEach(tab => {
    tab.classList.toggle('active', tab.dataset.tab === tabName);
  });

  logExploreOpen(tabName);
  loadTab(tabName);
}

// ─────────────────────────────────────────────────────────────────────────
// LOAD TAB CONTENT
// ─────────────────────────────────────────────────────────────────────────

async function loadTab(tabName) {
  const contentEl = currentOverlay?.querySelector('#explore-content');
  if (!contentEl) return;

  // Show loading
  contentEl.innerHTML = `
    <div class="explore-loading">
      <div class="explore-loading-spinner"></div>
      <p>Loading verses...</p>
    </div>
  `;

  let verses = [];

  try {
    switch (tabName) {
      case 'trending':
        verses = await getTrendingVerses(20);
        break;
      case 'recent':
        verses = await getRecentSharedVerses(30);
        break;
      case 'friends':
        if (!isAuthenticated()) {
          contentEl.innerHTML = renderAuthRequired();
          return;
        }
        verses = await getFriendsSharedVerses(30);
        break;
    }

    if (verses.length === 0) {
      contentEl.innerHTML = renderEmptyState(tabName);
    } else {
      contentEl.innerHTML = renderVerseList(verses, tabName);
      setupVerseActions(contentEl);
    }
  } catch (error) {
    console.error('Load tab error:', error);
    contentEl.innerHTML = `
      <div class="explore-empty">
        <p>Something went wrong. Please try again.</p>
      </div>
    `;
  }
}

// ─────────────────────────────────────────────────────────────────────────
// RENDER FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────

function renderVerseList(verses, tabName) {
  return `
    <div class="explore-verse-list">
      ${verses.map((verse, index) => renderVerseCard(verse, index, tabName)).join('')}
    </div>
  `;
}

function renderVerseCard(verse, index, tabName) {
  const user = getCurrentUser();
  const isLiked = verse.likedBy?.includes(user?.uid);

  return `
    <div class="explore-verse-card" data-verse-id="${verse.id}">
      <div class="explore-verse-content">
        <p class="explore-verse-text">"${truncateText(verse.text, 150)}"</p>
        <span class="explore-verse-reference">${verse.reference}</span>
      </div>
      
      <div class="explore-verse-meta">
        <span class="explore-verse-sharer">
          ${verse.sharedByName || 'Anonymous'}
        </span>
        
        <div class="explore-verse-actions">
          <button class="explore-action-btn like-btn ${isLiked ? 'liked' : ''}" data-verse-id="${verse.id}">
            ${icons.heart}
            <span>${verse.likes || 0}</span>
          </button>
          
          <button class="explore-action-btn save-btn" data-reference="${verse.reference}" data-text="${encodeURIComponent(verse.text)}">
            ${icons.saved}
          </button>
        </div>
      </div>
    </div>
  `;
}

function renderEmptyState(tabName) {
  const messages = {
    trending: {
      icon: '🔥',
      title: 'No trending verses yet',
      text: 'Be the first to share a verse with the community!'
    },
    recent: {
      icon: '🕐',
      title: 'No recent shares',
      text: 'The community is quiet. Share a verse to get things started!'
    },
    friends: {
      icon: '👥',
      title: 'No friend shares yet',
      text: 'Your friends haven\'t shared any verses. Invite them to join!'
    }
  };

  const msg = messages[tabName] || messages.trending;

  return `
    <div class="explore-empty">
      <div class="explore-empty-icon">${msg.icon}</div>
      <h3 class="explore-empty-title">${msg.title}</h3>
      <p class="explore-empty-text">${msg.text}</p>
    </div>
  `;
}

function renderAuthRequired() {
  return `
    <div class="explore-empty">
      <div class="explore-empty-icon">🔐</div>
      <h3 class="explore-empty-title">Sign in to see friends</h3>
      <p class="explore-empty-text">Connect with friends and see what verses resonate with them.</p>
      <button class="explore-auth-btn" id="explore-auth-btn">Sign in</button>
    </div>
  `;
}

function truncateText(text, maxLength) {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

// ─────────────────────────────────────────────────────────────────────────
// VERSE ACTIONS
// ─────────────────────────────────────────────────────────────────────────

function setupVerseActions(contentEl) {
  // Like buttons
  contentEl.querySelectorAll('.like-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();

      if (!isAuthenticated()) {
        showAuthPrompt(() => {
          // Retry after auth
          handleLike(btn);
        }, 'save');
        return;
      }

      await handleLike(btn);
    });
  });

  // Save buttons
  contentEl.querySelectorAll('.save-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();

      if (!isAuthenticated()) {
        showAuthPrompt(() => {
          handleSave(btn);
        }, 'save');
        return;
      }

      await handleSave(btn);
    });
  });

  // Auth button in empty state
  contentEl.querySelector('#explore-auth-btn')?.addEventListener('click', () => {
    showAuthPrompt(() => {
      loadTab('friends');
    }, 'save');
  });
}

async function handleLike(btn) {
  const verseId = btn.dataset.verseId;

  btn.disabled = true;

  const result = await likeSharedVerse(verseId);

  if (result.success) {
    btn.classList.toggle('liked', result.liked);

    // Update count
    const countSpan = btn.querySelector('span');
    if (countSpan) {
      const currentCount = parseInt(countSpan.textContent) || 0;
      countSpan.textContent = result.liked ? currentCount + 1 : Math.max(0, currentCount - 1);
    }

    // Animate
    btn.classList.add('animating');
    setTimeout(() => btn.classList.remove('animating'), 300);
  }

  btn.disabled = false;
}

async function handleSave(btn) {
  const reference = btn.dataset.reference;
  const text = decodeURIComponent(btn.dataset.text);

  btn.disabled = true;

  await saveFavorite({ reference, text });

  // Show feedback
  btn.classList.add('saved');
  showToast('Saved to your collection');

  btn.disabled = false;
}

function showToast(message) {
  const existing = document.querySelector('.toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.textContent = message;
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateX(-50%) translateY(20px)';
    setTimeout(() => toast.remove(), 300);
  }, 2500);
}

// ─────────────────────────────────────────────────────────────────────────
// EXPORT
// ─────────────────────────────────────────────────────────────────────────

export default { showExplore, hideExplore };
